import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { connect } from 'react-redux';

import * as BackstageConstants from '../../constants';
import * as D from '../../deps';
import { getUserStationId } from '../../utils';

import { getRadioOnlyModalConfig } from './ArtistRadioOnlyModal';

export function useClickControls(props) {
  const { integration, router } = useContext(D.AppContext);
  const {
    pandoraId,
    artistPlayId,
    shareableUrlPath,
    hasRadio,
    isArtistStationPlaying,
    nowPlayingUrl,
    topSongsHaveNoRights,
    openModal,
    closeModal,
    openShareModal,
    pauseTrack,
    startStation,
    playResumeSource,
  } = props;

  const logClickEvent = componentEventType => {
    integration.action.clicked({
      pandoraId,
      componentEventType,
      contentSection: BackstageConstants.BackstageSections.CONTROL_BAR,
    });
  };

  const playArtistStation = () => {
    logClickEvent(BackstageConstants.BackstageEvents.startArtistStation);
    if (isArtistStationPlaying) {
      // TODO: WEBCLIENT-10081 This might be better in the startStation use case, if we want clicking play on
      // a playing artist station to always route to NP
      router.push(nowPlayingUrl);
    } else {
      startStation({
        pandoraId,
        rerouteBasedOnTier: true,
        componentName: 'BackstageArtist',
        pageUrl: shareableUrlPath,
      });
    }
  };

  const onModalStartStationClick = () => {
    closeModal();
    playArtistStation();
  };

  const pauseTopSongs = () => {
    logClickEvent(BackstageConstants.BackstageEvents.pause);
    pauseTrack();
  };

  const playTopSongs = () => {
    if (topSongsHaveNoRights) {
      logClickEvent(BackstageConstants.BackstageEvents.insufficientRights);
      openModal(getRadioOnlyModalConfig({ hasRadio, onModalStartStationClick }));
      return;
    }
    logClickEvent(BackstageConstants.BackstageEvents.play);
    playResumeSource({
      id: artistPlayId,
      pandoraType: D.PandoraTypes.ARTIST_TOP_SONGS,
      context: D.Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
      pageUrl: shareableUrlPath,
    });
  };

  const shareArtist = () => {
    logClickEvent(BackstageConstants.BackstageEvents.share);
    openShareModal(null, {
      shareParentSourceId: pandoraId,
      shareParentSourceType: D.Constants.SourceTypes.PandoraTypes.ARTIST,
    });
  };

  return {
    playTopSongs,
    pauseTopSongs,
    playArtistStation,
    shareArtist,
    topSongsHaveNoRights,
  };
}

export function mapStateToProps(state, ownProps) {
  const isCuratorExperienceEnabled = D.Utils.TunerVars.curatorExperienceEnabled(state);
  if (isCuratorExperienceEnabled) {
    return {};
  }
  const { pandoraId } = ownProps;
  const details = D.getInDomain('catalog.utils.getCatalogDetailsFromState')(state, pandoraId);
  const annotation = D.getAnnotationsById(state, pandoraId) || BackstageConstants.emptyMap;
  const isAudioPlaying = D.getInDomain('playback.utils.isAudioPlaying', false);
  const isPlaylistOn = D.getInDomain('playback.utils.isSourcePlayingCurrently', false);
  const artistPlayId = details.get('artistPlayId', '');
  const topTracks = details
    .get('topTracks', BackstageConstants.emptyList)
    .slice(0, BackstageConstants.BackstageConstants.NUM_TOP_TRACKS_TO_RENDER);
  const annotatedTopSongs = D.getManyAnnotationsById(state, topTracks);
  const hasAnnotatedTopSongs = annotatedTopSongs.some(annotatedTopSong =>
    D.hasInteractiveRights(annotatedTopSong),
  );
  const userStationId = getUserStationId(state, pandoraId);

  return {
    artistPlayId,
    hasRadio: ownProps.hasRadio ?? annotation.get('hasRadio', false),
    shareableUrlPath: annotation.get('shareableUrlPath', ''),
    isArtistTopSongsPlaying: isAudioPlaying(state) && isPlaylistOn(state, artistPlayId),
    isArtistStationPlaying: userStationId && isPlaylistOn(state, userStationId),
    nowPlayingUrl: D.getStationPlayPathForStationCode(userStationId),
    topSongsHaveNoRights: !hasAnnotatedTopSongs,
    topSongsLooksDisabled: !topTracks.isEmpty() && !hasAnnotatedTopSongs,
    topSongsIsDisabled: topTracks.isEmpty(),
  };
}

const mapDispatchToProps = {
  openModal: D.openModal,
  closeModal: D.closeModal,
  openShareModal: D.Actions.Share.openShareModal,
  pauseTrack: D.Actions.UseCases.pauseTrack,
  startStation: D.Actions.UseCases.startStation,
  playResumeSource: D.playResumeSource,
};

export function withArtistButtonControls(WrappedComponent) {
  const ArtistButtonControls = props => {
    const { isArtistTopSongsPlaying, topSongsLooksDisabled, topSongsIsDisabled } = props;

    const { playTopSongs, pauseTopSongs, playArtistStation, shareArtist } = useClickControls(props);

    return (
      <WrappedComponent
        playTopSongs={playTopSongs}
        pauseTopSongs={pauseTopSongs}
        isArtistTopSongsPlaying={isArtistTopSongsPlaying}
        topSongsLooksDisabled={topSongsLooksDisabled}
        topSongsIsDisabled={topSongsIsDisabled}
        playArtistStation={playArtistStation}
        shareArtist={shareArtist}
        {...props}
      />
    );
  };

  ArtistButtonControls.propTypes = {
    pandoraId: PropTypes.string,
    artistPlayId: PropTypes.string,
    hasRadio: PropTypes.bool,
    nowPlayingUrl: PropTypes.string,
    shareableUrlPath: PropTypes.string,
    hasAnnotatedTopSongs: PropTypes.bool,
    isArtistTopSongsPlaying: PropTypes.bool,
    isCurrentArtistStationPlaying: PropTypes.bool,
    openModal: PropTypes.func,
    closeModal: PropTypes.func,
    openShareModal: PropTypes.func,
    pauseTrack: PropTypes.func,
    startStation: PropTypes.func,
    playResumeSource: PropTypes.func,
    isArtistStationPlaying: PropTypes.bool,
    topSongsHaveNoRights: PropTypes.bool,
    topSongsLooksDisabled: PropTypes.bool,
    topSongsIsDisabled: PropTypes.bool,
  };

  return connect(mapStateToProps, mapDispatchToProps)(ArtistButtonControls);
}
